import { useEffect, useState } from 'react';
import CallbackCTA from './CallbackCTA';
import { getRentalDescription, getToolName } from '../../data/toolLandingPages';

export default function ToolBottomSheet({ tool, language, t, onClose, onCallbackOpen }) {
  const [open, setOpen] = useState(false);
  const name = getToolName(tool, language);
  const desc = getRentalDescription(tool, language);
  const available = tool.available && !tool.maintenance;

  useEffect(() => {
    const id = requestAnimationFrame(() => setOpen(true));
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
      cancelAnimationFrame(id);
    };
  }, []);

  const handleClose = () => {
    setOpen(false);
    setTimeout(onClose, 280);
  };

  const prices = [
    // 4h tile temporarily hidden — restore the line below to bring it back
    tool.pricePerDay    > 0 && { label: t('rentPage.perDay'),   value: tool.pricePerDay, wide: true },
    tool.pricePerNight  > 0 && { label: t('rentPage.perNight'), value: tool.pricePerNight },
    tool.pricePerWeek   > 0 && { label: t('rentPage.perWeek'),  value: tool.pricePerWeek },
  ].filter(Boolean);

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end">
      <div
        className={`absolute inset-0 bg-black/50 transition-opacity duration-300 ${open ? 'opacity-100' : 'opacity-0'}`}
        onClick={handleClose}
        aria-hidden="true"
      />
      <div
        className={`relative bg-white rounded-t-2xl max-h-[85vh] flex flex-col transition-transform duration-300 ease-out ${open ? 'translate-y-0' : 'translate-y-full'}`}
        role="dialog"
        aria-modal="true"
        aria-label={name}
      >
        <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
          <div className="w-10 h-1 bg-gray-300 rounded-full" />
        </div>
        <button
          onClick={handleClose}
          className="absolute top-3 right-4 text-gray-400 hover:text-black text-2xl leading-none"
          aria-label={t('rentPage.sheetClose')}
        >
          ×
        </button>
        <div className="overflow-y-auto px-5 pb-8 pt-2">
          <div className="flex flex-col items-center text-center mb-5">
            {tool.image ? (
              <img
                src={tool.image}
                alt={name}
                width="96"
                height="96"
                className="w-24 h-24 object-contain mb-3"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            ) : (
              <div className="w-24 h-24 bg-gray-100 rounded-xl mb-3 flex items-center justify-center text-4xl" aria-hidden="true">🔧</div>
            )}
            <h3 className="text-[18px] font-bold text-black leading-tight">{name}</h3>
            <span className={`mt-2 text-[12px] px-3 py-1 rounded-full font-medium ${available ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-500'}`}>
              {available ? t('rentPage.available') : t('rentPage.unavailable')}
            </span>
          </div>
          {prices.length > 0 && (
            <div className="grid grid-cols-2 gap-2 mb-5">
              {prices.map(({ label, value, wide }) => (
                <div key={label} className={`bg-gray-50 rounded-xl p-3 text-center ${wide ? 'col-span-2' : ''}`}>
                  <div className="text-[20px] font-bold text-dewalt">{value}</div>
                  <div className="text-[11px] text-gray-500 mt-0.5">{label}</div>
                </div>
              ))}
            </div>
          )}
          <CallbackCTA t={t} onOpen={() => onCallbackOpen(name)} variant="yellow" placement="tool_sheet" />
          {desc && (
            <p className="text-[13px] text-gray-600 text-justify leading-relaxed mt-6">{desc}</p>
          )}
        </div>
      </div>
    </div>
  );
}
